import type { paths } from '@st/api/paths'
import type { ResponseError } from '@st/openapi-tools'
import { useAnalytics } from '@st/analytics/composables/useAnalytics'
import { delay, isTruthy } from '@st/utils'
import { useCasinoDeps } from '@st/casino/useDeps'
import { OPEN_GAME_ANIMATION_DURATION } from '../consts'

export type DemoInitError = ResponseError<
  paths,
  '/casino/game/init/demo',
  'post'
>
export type RealInitError = ResponseError<paths, '/casino/game/init', 'post'>
export type GameInitError = DemoInitError | RealInitError
export interface LauchGameState {
  gameId: number | null
  state: 'idle' | 'loading' | 'success' | 'error'
}

interface LauchGameOptions {
  gameId: number
  gameMode: 'real' | 'demo'
}

interface LaunchGameResult {
  result:
    | 'success'
    | 'initError'
    | 'authError'
    | 'geoError'
    | 'authErrorWithDemoAvailable'
    | 'geoErrorWithDemoAvaiable'
    | 'platformError'
  error?: GameInitError
}

interface GetCurrencyIdParams {
  currencyIds: number[]
  preferredId: number
  defaultId: number
}

interface GameData {
  hasDemoMode: boolean
  isWrongPlatform: boolean
  availableCurrencyIdsForGame: number[]
}

export const useGameInitStore = defineStore('gameInit', () => {
  const initError = ref<GameInitError | null>(null)
  const frameUrl = ref<string | null>(null)
  const sessionId = ref<string | null>(null)
  const gameMode = ref<'demo' | 'real' | null>(null)
  const gameCurrencyId = ref<number | null>(null)
  const gameData = ref<GameData>({
    hasDemoMode: false,
    isWrongPlatform: false,
    availableCurrencyIdsForGame: [],
  })
  function clearGameData() {
    gameData.value = {
      hasDemoMode: false,
      isWrongPlatform: false,
      availableCurrencyIdsForGame: [],
    }
  }

  const { sendEvent } = useAnalytics()

  const stFetch = useRawStFetch()
  const {
    userCurrencySettings,
    isAuthenticated,
    isReady: isReadyAuth,
    activeAccount,
    getCurrencyIdByCode,
    isCurrencyForTMA,
  } = useCasinoDeps()

  const { isReady: isReadyGeo, isAvailable } = useGeoAvailability()
  const url = useRequestURL()

  const { isTelegramMiniApp } = useApplicationType()

  function clear() {
    initError.value = null
    frameUrl.value = null
    sessionId.value = null
    gameMode.value = null
  }

  const defaultGameCurrencyIds = computed(() => {
    const preferredCode =
      userCurrencySettings.value?.preferredCurrencyCodeForCasino
    const defaultId = userCurrencySettings.value?.defaultCurrencyIdForCasinoGame

    return {
      preferredId: getCurrencyIdByCode(preferredCode) ?? defaultId,
      defaultId,
    }
  })

  function getGameCurrencyId({
    currencyIds,
    preferredId,
    defaultId,
  }: GetCurrencyIdParams): number {
    const isCurrencyForGame = currencyIds.includes(preferredId)
    if (!isCurrencyForGame) return defaultId

    if (isTelegramMiniApp.value) {
      return isCurrencyForTMA(preferredId) ? preferredId : defaultId
    }

    return preferredId
  }

  async function initRealGame(gameId: number) {
    await until(
      () =>
        !!defaultGameCurrencyIds.value.defaultId && !!activeAccount.value?.id,
    ).toBeTruthy()

    gameCurrencyId.value = getGameCurrencyId({
      currencyIds: gameData.value.availableCurrencyIdsForGame,
      preferredId: defaultGameCurrencyIds.value.preferredId!,
      defaultId: defaultGameCurrencyIds.value.defaultId!,
    })

    const { data, error } = await stFetch('/casino/game/init', {
      method: 'post',
      body: {
        gameId,
        gameCurrencyId: gameCurrencyId.value,
        accountId: activeAccount.value!.id,
        historyUrl: `${url.origin}/personal/bets`,
        depositUrl: `${url.origin}/?modal=payments`,
        returnUrl: `${url.origin}/casino`,
      },
    })

    if (error) {
      initError.value = error
    } else {
      clear()
      sessionId.value = data.sessionId
      frameUrl.value = data.url
      gameMode.value = 'real'

      sendEvent('initGame', {
        sessionId: data.sessionId,
        gameId,
        gameMode: 'real',
      })
    }
  }

  async function initDemoGame(gameId: number) {
    const { data, error } = await stFetch('/casino/game/init/demo', {
      method: 'post',
      body: {
        gameId,
        returnUrl: `${url.origin}/casino`,
      },
    })

    if (error) {
      initError.value = error
    } else {
      clear()
      gameMode.value = 'demo'
      frameUrl.value = data.url

      sendEvent('initGame', {
        gameId,
        gameMode: 'demo',
      })
    }
  }

  const { platform } = usePlatform()
  const platformParams: {
    forMobile?: boolean
    forDesktop?: boolean
  } = platform.value === 'desktop' ? { forDesktop: true } : { forMobile: true }

  /**
   * Проверяем игру на доступность и получаем необходимые данные для запуска
   * 1. Если игра доступна, то проверяем доступность демо режима (hasDemoMode)
   * Если игра доступна, но недоступен демо режим (модалка с авторизацией)
   * 3. Если игра доступна, отдаем доступные валюты для игры
   * 4. Если игра недоступна (модалка с переходом на другую платформу)
   */
  async function checkGame(gameId: number) {
    clearGameData()

    const {
      data: { data: games },
    } = await stFetch('/casino/game/find', {
      method: 'post',
      body: {
        pagination: {
          page: 0,
          perPage: 1,
          orderBy: [
            {
              fieldName: 'gameId',
              sortOrder: 'ASC',
            },
          ],
        },
        params: {
          gameId: [gameId],
          ...platformParams,
        },
      },
    })

    const filteredAvailableCurrencyIdsForGame =
      games?.[0]?.availableCurrencyIdsForGame?.filter(isTruthy) ?? []
    const filteredForMiniApp = isTelegramMiniApp.value
      ? filteredAvailableCurrencyIdsForGame.filter((id) => isCurrencyForTMA(id))
      : filteredAvailableCurrencyIdsForGame

    gameData.value = {
      hasDemoMode: Boolean(games[0]?.hasDemoMode),
      isWrongPlatform: !games.length,
      // Тут у бэка в спеке availableCurrencyIdsForGame может содержать null
      // поэтому фильтруем null значения из массива (что очень странно)
      availableCurrencyIdsForGame: filteredForMiniApp,
    }
  }

  async function launchGame(
    options: LauchGameOptions,
  ): Promise<LaunchGameResult> {
    const { hasDemoMode, isWrongPlatform } = gameData.value

    if (options.gameMode === 'demo') {
      await initDemoGame(options.gameId)
      if (initError.value) {
        return {
          result: 'initError',
          error: initError.value,
        }
      }
      return {
        result: 'success',
      }
    }

    await until(isReadyGeo).toBeTruthy()
    if (!isAvailable.value) {
      if (hasDemoMode) {
        return {
          result: 'geoErrorWithDemoAvaiable',
        }
      }

      return {
        result: 'geoError',
      }
    }

    await until(isReadyAuth).toBeTruthy()
    if (!isAuthenticated.value) {
      if (hasDemoMode) {
        return {
          result: 'authErrorWithDemoAvailable',
        }
      }

      return {
        result: 'authError',
      }
    }

    if (isWrongPlatform) {
      return {
        result: 'platformError',
      }
    }

    await initRealGame(options.gameId)

    if (initError.value) {
      return {
        result: 'initError',
        error: initError.value,
      }
    }
    return {
      result: 'success',
    }
  }

  const launchingGameState = ref<LauchGameState>({
    gameId: null,
    state: 'idle',
  })
  async function launchGameWithStates(
    options: LauchGameOptions,
  ): Promise<LaunchGameResult> {
    await checkGame(options.gameId)

    launchingGameState.value.gameId = options.gameId
    launchingGameState.value.state = 'loading'

    const launchResult = await launchGame(options)

    if (launchResult.result === 'success') {
      launchingGameState.value.state = 'success'
      // wait for loading animation
      await delay(OPEN_GAME_ANIMATION_DURATION)
    } else launchingGameState.value.state = 'error'

    launchingGameState.value.gameId = null
    launchingGameState.value.state = 'idle'
    return launchResult
  }
  const isGameLoading = computed(
    () => launchingGameState.value.state === 'loading',
  )

  return {
    launchGame,
    launchGameWithStates,
    frameUrl,
    sessionId,
    gameMode,
    initError,
    launchingGameState,
    checkGame,
    isGameLoading,
    gameCurrencyId,
    gameData,
  }
})
